import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auditorAPI } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { Eye, Users, Search } from 'lucide-react';

const AuditorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userStats, setUserStats] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadUserStats();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      loadBatches(selectedUser.user_id, filterStatus);
    } else {
      loadBatches(null, filterStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, filterStatus]);

  const loadUserStats = async () => {
    try {
      setLoading(true);
      const response = await auditorAPI.getUserStats();
      setUserStats(response.data);
    } catch (error) {
      console.error('Error loading user stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBatches = async (userId = null, status = 'all') => {
    try {
      const response = await auditorAPI.getBatches(userId, status);
      setBatches(response.data);
    } catch (error) {
      console.error('Error loading batches:', error);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(selectedUser?.user_id === user.user_id ? null : user);
  };

  // Filter users based on search query
  const filteredUserStats = userStats.filter((user) => {
    const query = searchQuery.toLowerCase();
    return (
      user.full_name.toLowerCase().includes(query) ||
      user.username.toLowerCase().includes(query) ||
      user.company_name.toLowerCase().includes(query)
    );
  });

  const styles = `
    .page-header {
      margin-bottom: 32px;
    }

    .page-title {
      font-size: 28px;
      font-weight: 800;
      color: hsl(var(--foreground));
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .page-subtitle {
      font-size: 14px;
      color: hsl(var(--muted-foreground));
    }

    .filter-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      background: var(--gradient-card);
      border-radius: var(--radius-lg);
      border: 1px solid hsl(var(--border));
      margin-bottom: 20px;
    }

    .filter-label {
      fontSize: 14px;
      fontWeight: 500;
      color: hsl(var(--muted-foreground));
    }

    .filter-select {
      padding: 8px 16px;
      fontSize: 14px;
      border: 1px solid hsl(var(--border));
      borderRadius: var(--radius-md);
      background: hsl(var(--card));
      color: hsl(var(--foreground));
      cursor: pointer;
      outline: none;
      fontFamily: var(--font-primary);
    }

    .content-grid {
      display: grid;
      grid-template-columns: 280px 1fr;
      gap: 20px;
    }

    .sidebar-card {
      background: var(--gradient-card);
      border: 1px solid hsl(var(--border));
      border-radius: var(--radius-lg);
      padding: 20px;
      height: fit-content;
      max-height: calc(100vh - 280px);
      overflow-y: auto;
    }

    .sidebar-title {
      fontSize: 14px;
      fontWeight: 600;
      color: hsl(var(--foreground));
      marginBottom: 16px;
    }

    .search-input {
      width: 100%;
      padding: 8px 12px;
      fontSize: 13px;
      border: 1px solid hsl(var(--border));
      borderRadius: var(--radius-md);
      outline: none;
      marginBottom: 12px;
      fontFamily: var(--font-primary);
    }

    .search-input:focus {
      border-color: hsl(var(--ring));
      box-shadow: 0 0 0 3px hsl(var(--ring) / 0.1);
    }

    .user-item {
      padding: 12px;
      borderRadius: var(--radius-md);
      marginBottom: 8px;
      cursor: pointer;
      transition: all 0.2s;
    }

    .user-item:hover {
      background: hsl(var(--secondary) / 0.5);
    }

    .user-item.active {
      border: 1px solid hsl(var(--primary));
      background: hsl(var(--primary) / 0.1);
    }

    .content-card {
      background: var(--gradient-card);
      border-radius: var(--radius-lg);
      border: 1px solid hsl(var(--border));
      box-shadow: var(--shadow-card);
      overflow: hidden;
    }

    .card-header {
      padding: 16px 20px;
      border-bottom: 1px solid hsl(var(--border));
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .card-title {
      font-size: 16px;
      font-weight: 600;
      color: hsl(var(--foreground));
      margin: 0;
    }

    .data-table {
      width: 100%;
      border-collapse: collapse;
    }

    .data-table thead {
      background: hsl(var(--secondary) / 0.5);
    }

    .data-table th {
      padding: 14px 24px;
      text-align: left;
      font-size: 11px;
      font-weight: 700;
      color: hsl(var(--muted-foreground));
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .data-table tbody tr {
      border-bottom: 1px solid hsl(var(--border));
      transition: background 0.2s;
    }

    .data-table tbody tr:hover {
      background: hsl(var(--secondary) / 0.3);
    }

    .data-table td {
      padding: 16px 24px;
      font-size: 14px;
      color: hsl(var(--foreground));
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
    }

    .empty-icon {
      font-size: 48px;
      opacity: 0.3;
      margin-bottom: 16px;
    }

    .loading-container {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: hsl(var(--background));
    }

  `;

  if (loading && userStats.length === 0) {
    return (
      <>
        <style>{styles}</style>
        <div className="loading-container">
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
            <div style={{ fontSize: '14px', color: 'hsl(var(--muted-foreground))' }}>
              Loading Auditor Dashboard...
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <DashboardLayout user={user} logout={logout}>
        <div className="page-header">
          <h1 className="page-title">
            <Eye size={32} />
            Auditor Dashboard
          </h1>
          <p className="page-subtitle">Review and approve vendor batch uploads</p>
        </div>

        {/* Filter Section */}
        <div className="filter-bar">
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'hsl(var(--foreground))', margin: 0 }}>
            Batch Reviews
          </h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <label className="filter-label">Filter:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Batches</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="rework">Rework</option>
            </select>
          </div>
        </div>

        {/* Content Grid */}
        <div className="content-grid">
          {/* Sidebar - Vendor Users */}
          <div className="sidebar-card">
            <div className="sidebar-title">
              <Users size={16} style={{ display: 'inline', marginRight: '8px' }} />
              Vendor Users ({userStats.length})
            </div>

            {/* Search Bar */}
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'hsl(var(--muted-foreground))' }} />
              <input
                type="text"
                placeholder="Search by name or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
                style={{ paddingLeft: '36px' }}
              />
            </div>

            {filteredUserStats.map((userData) => {
              const isActive = selectedUser?.user_id === userData.user_id;
              return (
                <div
                  key={userData.user_id}
                  onClick={() => handleUserClick(userData)}
                  className={`user-item ${isActive ? 'active' : ''}`}
                  style={{ border: isActive ? '' : '1px solid transparent' }}
                >
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'hsl(var(--foreground))',
                    marginBottom: '2px'
                  }}>
                    {userData.company_name}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'hsl(var(--muted-foreground))',
                    marginBottom: '10px'
                  }}>
                    {userData.full_name}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Main Panel - Batches */}
          <div className="content-card">
            <div className="card-header">
              <h2 className="card-title">
                {selectedUser ? `Batches for ${selectedUser.full_name}` : 'All Validated Batches'}
              </h2>
              {selectedUser && (
                <button
                  onClick={() => setSelectedUser(null)}
                  className="btn-primary"
                  style={{ padding: '6px 12px', fontSize: '13px' }}
                >
                  Clear Filter
                </button>
              )}
            </div>

            <div style={{ overflowX: 'auto' }}>
              {batches.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📦</div>
                  <p style={{ color: 'hsl(var(--muted-foreground))' }}>No validated batches found</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Batch Name</th>
                      <th>Uploaded By</th>
                      <th>Images</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Rejection Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((batch) => (
                      <tr key={batch.batch_id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span
                              onClick={() => navigate(`/batch/${batch.batch_id}`)}
                              style={{
                                color: 'hsl(var(--info))',
                                fontWeight: '500',
                                cursor: 'pointer',
                                textDecoration: 'underline'
                              }}
                            >
                              {batch.batch_name}
                            </span>
                            {batch.is_rework && (
                              <span className="badge badge-warning">
                                Rework
                              </span>
                            )}
                          </div>
                        </td>
                        <td>{batch.uploaded_by_name}</td>
                        <td>
                          <div style={{ fontWeight: '500', color: 'hsl(var(--success))' }}>
                            {batch.passed_images} passed
                          </div>
                          <div style={{ fontSize: '12px', color: 'hsl(var(--muted-foreground))' }}>
                            {batch.total_images} total
                          </div>
                        </td>
                        <td>
                          {new Date(batch.uploaded_at).toLocaleDateString()}
                        </td>
                        <td>
                          <span className={`batch-status ${batch.auditor_status || 'waiting'}`}>
                            {batch.auditor_status || 'Pending'}
                          </span>
                        </td>
                        <td>
                          {batch.rejection_reason ? (
                            <span style={{ fontSize: '13px', color: 'hsl(var(--destructive))', fontStyle: 'italic' }}>
                              {batch.rejection_reason}
                            </span>
                          ) : (
                            <span style={{ color: 'hsl(var(--muted-foreground))' }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </DashboardLayout>
    </>
  );
};

export default AuditorDashboard;
